import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Mail, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { BrandMark } from "@/components/AppShell";
import { roleHome } from "@/components/ProtectedRoute";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/patient";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("email"); // "email" | "whatsapp"
  const [phone, setPhone] = useState("");
  const [waStep, setWaStep] = useState("phone"); // "phone" | "code"
  const [waCode, setWaCode] = useState("");
  const [waName, setWaName] = useState("");
  const [waSending, setWaSending] = useState(false);

  const errorFromState = location?.state?.error;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name || user.email}`);
      navigate(roleHome(user.role), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns 403 with { code: 'email_not_verified', email, message } for unverified users.
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        toast.info("Please verify your email to continue.");
        navigate("/verify-email", { state: { email: detail.email || email } });
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const sendWaOtp = async () => {
    if (!phone.trim()) return;
    setWaSending(true);
    try {
      const { data } = await api.post("/auth/whatsapp/send", {
        phone: phone.trim(),
        channel: "whatsapp",
        consent_terms: true,
        consent_privacy: true,
      });
      if (data?.ok === false && data?.status === "not_configured") {
        toast.error(data.hint || "WhatsApp login is not configured yet.");
        return;
      }
      toast.success(`Code sent on WhatsApp to ${data.phone}`);
      setWaStep("code");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWaSending(false);
    }
  };

  const verifyWaOtp = async () => {
    if (!waCode.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/whatsapp/verify", {
        phone: phone.trim(),
        code: waCode.trim(),
        name: waName.trim() || undefined,
      });
      await refresh?.();
      toast.success(`Welcome, ${data.name || data.phone}`);
      navigate(roleHome(data.role), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left */}
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-25" />
        <div className="relative h-full p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/"><BrandMark /></Link>
          <div>
            <div className="overline text-primary-foreground/70">Trust infrastructure</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight">
              A verified doctor, a certified hospital,<br /> a confident patient.
            </h2>
            <p className="mt-4 text-primary-foreground/80 max-w-md">
              Sign in to your Arogya Pramana workspace — your role automatically routes you to the right portal.
            </p>
          </div>
          <div className="text-xs text-primary-foreground/60">
            Not a review platform. A verification layer for Indian healthcare.
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/"><BrandMark /></Link>
          </div>
          <div className="overline">Sign in</div>
          <h1 className="mt-2 font-serif text-4xl text-primary">Welcome back.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Use your Arogya credentials or continue with Google.
          </p>

          {errorFromState && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="login-error-state">
              {errorFromState}
            </div>
          )}

          <div className="mt-8">
            <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40" role="tablist" data-testid="login-mode-tabs">
              <button
                type="button"
                onClick={() => setMode("email")}
                className={`px-3 py-1.5 text-xs rounded ${mode === "email" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
                data-testid="login-mode-email"
              >
                Email + password
              </button>
              <button
                type="button"
                onClick={() => setMode("whatsapp")}
                className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1 ${mode === "whatsapp" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
                data-testid="login-mode-whatsapp"
              >
                <MessageCircle size={12} /> WhatsApp OTP
              </button>
            </div>
          </div>

          {mode === "email" ? (
          <form onSubmit={onSubmit} className="mt-4 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
                placeholder="you@example.in"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="login-submit-button">
              {submitting ? "Signing in…" : (<>Sign in <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>
          ) : (
          <div className="mt-4 space-y-4" data-testid="login-whatsapp">
            <div className="space-y-2">
              <Label htmlFor="wa-phone">Phone number</Label>
              <Input
                id="wa-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                inputMode="tel"
                data-testid="login-wa-phone-input"
                placeholder="+91 9XXXX XXXXX"
                disabled={waStep === "code"}
              />
              <p className="text-[11px] text-muted-foreground">
                Include the country code (default +91 for India).
              </p>
            </div>
            {waStep === "code" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="wa-name">Your name <span className="text-muted-foreground">(new users)</span></Label>
                  <Input
                    id="wa-name"
                    value={waName}
                    onChange={(e) => setWaName(e.target.value)}
                    autoComplete="name"
                    data-testid="login-wa-name-input"
                    placeholder="Anjali Kumar"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wa-code">6-digit code</Label>
                  <Input
                    id="wa-code"
                    inputMode="numeric"
                    maxLength={8}
                    value={waCode}
                    onChange={(e) => setWaCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="login-wa-code-input"
                    placeholder="123456"
                  />
                </div>
              </>
            )}
            {waStep === "phone" ? (
              <Button
                onClick={sendWaOtp}
                type="button"
                className="w-full h-11"
                disabled={waSending || !phone.trim()}
                data-testid="login-wa-send"
              >
                {waSending ? "Sending code…" : (<>Send code on WhatsApp <MessageCircle size={16} className="ml-2" /></>)}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setWaStep("phone")} type="button" data-testid="login-wa-back">
                  Change number
                </Button>
                <Button onClick={verifyWaOtp} type="button" className="flex-1 h-11" disabled={submitting || !waCode.trim()} data-testid="login-wa-verify">
                  {submitting ? "Verifying…" : (<>Verify & sign in <ArrowRight size={16} className="ml-2" /></>)}
                </Button>
              </div>
            )}
          </div>
          )}

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex-1 h-px bg-border" />
            <span className="uppercase tracking-widest">or</span>
            <span className="flex-1 h-px bg-border" />
          </div>

          <Button
            variant="outline"
            className="w-full h-11 border-primary text-primary hover:bg-primary/5"
            onClick={loginWithGoogle}
            data-testid="google-signin-button"
          >
            <Mail size={16} className="mr-2" />
            Continue with Google
          </Button>

          <div className="mt-6 rounded-md bg-muted p-4 text-xs text-muted-foreground leading-relaxed" data-testid="demo-credentials-panel">
            <div className="font-semibold text-primary mb-1 uppercase tracking-wider text-[10px]">Demo accounts</div>
            <div><span className="font-medium">Patient:</span> patient@demo.in / Patient@123</div>
            <div><span className="font-medium">Doctor:</span> doctor@demo.in / Doctor@123</div>
            <div><span className="font-medium">Hospital:</span> hospital@demo.in / Hospital@123</div>
            <div><span className="font-medium">Admin:</span> admin@arogyapramana.in / Admin@Arogya2026</div>
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
